import React, { useMemo } from 'react';
import {
  DEFAULT_THEME_COLOR,
} from 'appenv';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Routes from 'Routes';
import XtraBreadcrumbs from 'components/XtraBreadcrumbs';
import { BoothTaxonomy } from 'models/cms/boothTaxonomies';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { selectLocale } from 'models/localization';
import { useSelector } from 'react-redux';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import FilterCategoryContainer from './FilterCategoryContainer';

const HallFilterContainer = styled.div`
  width: 100%;
  max-width: 300px;
  height: 100%;
  overflow: auto;
  padding: 24px;
  color: #282B40;
  background: rgba(255, 255, 255, 0.8);
  z-index: 1;

  @media only screen and (max-width: 959px) {
    display: none;
  }
`;

const FilterTitleContainer = styled.div`
  width: 100%;
`;

const FilterText = styled.span`
  font-weight: bold;
  font-size: 18px;
  line-height: 18px;
  padding: 12px 0;
  display: inline-block;
  text-transform: uppercase;
`;

const ClearAllText = styled.span`
  font-size: 16px;
  line-height: 16px;
  letter-spacing: 0.4px;
  cursor: pointer;
  transition: all 0.1s;
  display: block;
  padding: 12px 0;
  float: right;

  &:hover {
    color: ${({ color }) => color};
  }
`;

const FilterWrapper = styled.div`
  display: block;
`;

const StyledXtraBreadcrumbs = styled(XtraBreadcrumbs)`
  padding: 0;
`;

export interface GenericCMSBoothFilterProps {
  filterOptions: BoothTaxonomy[];
  selectedFilter: string[];
  setSelectedFilter: (updater: string[] | ((filter: string[]) => string[])) => null;
  resetSelectedFilter: () => null;
  isFetchingFilter?: boolean;
  showCollapse: boolean[];
}

const GenericCMSBoothFilter: React.FC<GenericCMSBoothFilterProps> = ({
  filterOptions,
  selectedFilter,
  setSelectedFilter,
  resetSelectedFilter,
  isFetchingFilter,
  showCollapse,
}: GenericCMSBoothFilterProps) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery('(min-width: 960px)', { defaultMatches: true });
  const locale = useSelector(selectLocale);
  const { themeColor, pageNames: { lobby, hall } } = useSelector((s: any) => s.eventConfigs);
  const totalSelectedText = useMemo(
    () => (
      selectedFilter.length
        ? ` (${selectedFilter.length})`
        : ''
    ),
    [selectedFilter.length],
  );

  const lobbyName = lobby?.[locale] || t('page.lobby', 'Lobby');
  const hallName = hall?.[locale] || t('page.hall', 'Exhibition Hall');

  if (!isDesktop) return null;
  return (
    <HallFilterContainer>
      {isFetchingFilter && <CircularLoadingIndicator />}
      {!isFetchingFilter && (
        <>
          <StyledXtraBreadcrumbs paths={[{ name: lobbyName, to: Routes.lobby }, { name: hallName }]} />
          <FilterTitleContainer>
            <FilterText>
              {`${t('filter.title', 'Filter')}${totalSelectedText}`}
            </FilterText>
            <ClearAllText onClick={resetSelectedFilter} color={themeColor || DEFAULT_THEME_COLOR}>
              {t('filter.clear_all', 'Clear all')}
            </ClearAllText>
          </FilterTitleContainer>
          <FilterWrapper>
            {filterOptions.map((filter, index) => (
              <FilterCategoryContainer
                key={filter.id}
                filter={filter}
                selectedFilter={selectedFilter}
                setSelectedFilter={setSelectedFilter}
                showCollapse={showCollapse[index]}
              />
            ))}
          </FilterWrapper>
        </>
      )}
    </HallFilterContainer>
  );
};

export default GenericCMSBoothFilter;
